import { backgroundTransitionOnClick, Flex, flex, IconFont, pointer, singleTextEllipsis } from '@byecode/ui'
import type { DataSourceAbstract, FieldADTValue } from '@lighthouse/core'
import { ScrollArea } from '@mantine/core'
import cls from 'classnames'
import React, { useDeferredValue, useMemo } from 'react'
import styled from 'styled-components'

import { useFieldBlockContext } from '../../../../contexts'
import { useAtomData } from '../../../../hooks'
import { isEmptyCellValue, selectRecordFromPool } from '../../../../utils'
import FieldValuePreviewer from '../../../FieldValuePreviewer'

const SCxText = styled.span<{ color?: string }>`
    font-size: var(--font-size-normal);
    color: ${({ color }) => color ?? 'var(--color-black)'};
    line-height: 16px;
    ${pointer}
    ${singleTextEllipsis}
`

const SCxSelectItem = styled.div`
    width: 100%;
    padding: 12px 16px;
    ${flex}
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    /* height: 38px;
    line-height: 38px; */
    ${backgroundTransitionOnClick}
    font-size: var(--font-size-normal);
    &.disabled {
        cursor: not-allowed;
        opacity: 0.5;
        ${SCxText} {
            color: var(--color-gray-400);
        }
    }
`

const SCxIcon = styled(IconFont)<{ size?: number; color?: string; bgColor?: string; cursor?: string }>`
    font-size: ${({ size }) => size ?? 16}px;
    color: ${({ color }) => color ?? 'var(--color-black)'};
    cursor: ${({ cursor }) => cursor ?? 'pointer'};
`

const SCxLeftFill = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow: hidden;
`

const SCxRightFill = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`

const RelatedRecorderContent = styled(ScrollArea)<{ height?: number }>`
    position: relative;
    overflow: visible;
    width: 100%;
    display: flex;
    justify-content: stretch;
    max-height: 100%;

    & .mantine-ScrollArea-viewport {
        height: auto;

        max-height: ${({ height }) => (height ? `${height}px` : '100%')};
        > * {
            display: block !important;
            max-width: 100%;
        }
    }

    &.driver {
        ${SCxSelectItem}:not(:first-child) {
            border-top: 1px solid var(--color-gray-200);
        }
    }
`

const SCxNextRow = styled(Flex)`
    height: 22px;
    width: max-content;
    > * {
        width: auto !important;
    }
`

interface FieldListItemProps {
    recordId: string
    dataSource: DataSourceAbstract
    value: string[]
    disabled?: boolean
    onChange: (val: string) => void
}

export const FieldListItem: React.FC<FieldListItemProps> = ({ recordId, dataSource, value, disabled, onChange }) => {
    const { id: dsId, schema } = dataSource ?? {}
    const { appId, recordPoolAtom } = useFieldBlockContext()
    const selector = useMemo(() => selectRecordFromPool(dsId, recordId), [dsId, recordId])
    const record = useDeferredValue(useAtomData(recordPoolAtom, selector))

    if (!record) {
        return null
    }
    const { content } = record

    const [first, ...next] = dataSource?.viewOptions.tableProps?.filter(v => v.visible) ?? []
    const firstCellValue = content?.[first.id]
    const firstField = schema?.[first.id]
    const isEmptyOfFirst = firstCellValue ? isEmptyCellValue({ ...firstField, value: firstCellValue } as FieldADTValue) : true

    const isNextEmpty = next.every(v => {
        const cellValue = content?.[v.id]
        const field = schema?.[v.id]
        return cellValue ? isEmptyCellValue({ ...field, value: cellValue } as FieldADTValue) : true
    })
    if (!first || !schema) {
        return null
    }

    return (
        <SCxSelectItem className={cls({ disabled })} onClick={() => !disabled && onChange(recordId)}>
            <SCxLeftFill>
                {isEmptyOfFirst ? (
                    <div key={recordId}>-</div>
                ) : (
                    <div style={{ height: 22 }}>
                        <FieldValuePreviewer
                            isHasCurrentUser
                            // width={140}
                            enableEdit={false}
                            previewHost="lookupTable"
                            field={schema?.[first.id]}
                            data={content?.[first.id]?.value}
                            isWrap={false}
                            visiblePersonDescribe={false}
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        />
                    </div>
                )}

                {next.length > 0 &&
                    (isNextEmpty ? (
                        <div>-</div>
                    ) : (
                        <SCxNextRow alignItems="center" gap={12}>
                            {next.map(v => {
                                const cellValue = content?.[v.id]
                                const field = schema?.[v.id]
                                const isEmpty = cellValue ? isEmptyCellValue({ ...field, value: cellValue } as FieldADTValue) : true
                                if (isEmpty) {
                                    return null
                                }
                                return (
                                    <FieldValuePreviewer
                                        isHasCurrentUser
                                        previewHost="lookupTable"
                                        enableEdit={false}
                                        key={v.id}
                                        field={field}
                                        data={content?.[v.id]?.value}
                                        isWrap={false}
                                        visiblePersonDescribe={false}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    />
                                )
                            })}
                        </SCxNextRow>
                    ))}
            </SCxLeftFill>
            <SCxRightFill>{value.includes(recordId) && <SCxIcon type="Tick" color="var(--color-app-main)" />}</SCxRightFill>
        </SCxSelectItem>
    )
}
